import AdditionalGuestsPopup from 'components/Common/AdditionalGuestsPopup/AdditionalGuestsPopup'
import LoadingIndicator from 'components/Common/Loading/LoadingIndicator'
import TextButton from 'components/Luxkit/Button/TextButton'
import LineEditAltIcon from 'components/Luxkit/Icons/line/LineEditAltIcon'
import LinePlaneDepartureIcon from 'components/Luxkit/Icons/line/LinePlaneDepartureIcon'
import LinePlaneFlyIcon from 'components/Luxkit/Icons/line/LinePlaneFlyIcon'
import PriceRowCaption from 'components/Luxkit/PricePoints/PriceRowCaption'
import PriceRowPriceCaption from 'components/Luxkit/PricePoints/PriceRowPriceCaption'
import PriceRowTaxesAndFees from 'components/Luxkit/PricePoints/PriceRowTaxesAndFees'
import PriceRowValueDiscountWithCaption from 'components/Luxkit/PricePoints/Value/PriceRowValueDiscountWithCaption'
import AtolProtectedTooltip from 'components/OfferPage/AtolProtectedTooltip'
import Group from 'components/utils/Group'
import config from 'constants/config'
import { MINIMUM_DISCOUNT_TO_SHOW_PERCENTAGE_BADGE } from 'constants/content'
import { OfferPageActions, OfferPageFilterType } from 'contexts/OfferPage/offerPageStateReducer'
import { withOfferPageAndReduxState } from 'contexts/OfferPage/withOfferPageState'
import useOfferPageDispatch from 'hooks/OfferPage/useOfferPageDispatch'
import { hasIncludedGuestsExceeded } from 'lib/checkout/cartReservationUtils'
import { pluralizeToString } from 'lib/string/pluralize'
import LuxPlusPriceStack from 'luxPlus/components/LuxPlusPriceStack'
import moment from 'moment'
import React, { useCallback, useMemo } from 'react'
import { FlightsInfoByRoom, getAvailablePackagesByRoom, getCalendarsToBeFetchedByRoom, getFlightsInfoByRoom, isLoadingByRoom, LoadingStatusByRoom, PackagesOptionsViewsByRoom } from 'selectors/offerPage/multipleRoomBookingSelectors'
import { OfferPackageOptionView } from 'selectors/offerPage/offerPageSelectors'

interface MappedProps {
  offer: App.Offer;
  checkIn?: string;
  airports: Array<App.Airport>;
  flightOrigin?: App.Airport;
  isFlightsSelected?: boolean;
  isLoadingByRoom: LoadingStatusByRoom;
  flightsInfoByRoom: FlightsInfoByRoom;
  availablePackagesByRoom: PackagesOptionsViewsByRoom;
  calendarsToBeFetchedByRoom: LoadingStatusByRoom;
}

interface Props {
  room: App.RoomOccupants;
  packageOption: OfferPackageOptionView;
  footerNode?: React.ReactNode;
}

function MobileMultipleRoomsBookingPriceDetails(props: Props & MappedProps) {
  const {
    offer,
    checkIn,
    airports,
    flightOrigin,
    isFlightsSelected,
    isLoadingByRoom,
    flightsInfoByRoom,
    availablePackagesByRoom,
    calendarsToBeFetchedByRoom,
    room,
    packageOption,
    footerNode,
  } = props

  const {
    package: pkg,
    flightPrice,
    hotelValue,
    hotelDiscountPercent,
    bundledDiscountPercent,
    hotelPrice,
    hotelMemberPrice,
    baseMemberPrice,
    propertyFees,
  } = packageOption

  const offerPageDispatch = useOfferPageDispatch()
  const hasFlights = !!flightPrice && !offer.flightCacheDisabled
  const showIncludedGuestsExceeded = useMemo(() => hasIncludedGuestsExceeded(room, pkg, offer) && !!pkg.roomPolicyDescription, [pkg, room, offer])
  const flightDestination = useMemo(() => airports?.find(({ code }) => code === offer.flightDestinationPort), [airports, offer.flightDestinationPort])

  const isFlightsEnabled = flightsInfoByRoom[room.roomId]?.isFlightsAvailable
  const isFetching = isLoadingByRoom[room.roomId] || calendarsToBeFetchedByRoom[room.roomId]
  const availablePackagesCount = availablePackagesByRoom[room.roomId].length

  const onToggleFilter = useCallback((e: React.MouseEvent<HTMLButtonElement> | React.MouseEvent<HTMLAnchorElement>) => {
    e.stopPropagation()
    offerPageDispatch({ type: OfferPageActions.setRoomType, room: pkg.roomType })
    offerPageDispatch({ type: OfferPageActions.toggleFilter, filter: e.currentTarget.dataset.filter as OfferPageFilterType })
  }, [offerPageDispatch, pkg.roomType])

  const shouldShowDiscountPercent = hotelDiscountPercent >= MINIMUM_DISCOUNT_TO_SHOW_PERCENTAGE_BADGE
  const flightsBundledNoCache = !hasFlights && offer.bundledWithFlightsOnly
  const shouldShowBundledDiscountPercent = bundledDiscountPercent >= MINIMUM_DISCOUNT_TO_SHOW_PERCENTAGE_BADGE
  const highestDiscountPercent = hotelDiscountPercent > bundledDiscountPercent ? hotelDiscountPercent : bundledDiscountPercent

  const checkInMoment = useMemo(() => checkIn ? moment(checkIn) : undefined, [checkIn])
  const departureTooFarInFuture = useMemo(() => !!checkInMoment && checkInMoment.diff(moment.utc(), 'days') > 330, [checkInMoment])

  const totalPrice = hotelPrice + (propertyFees ?? 0)
  const totalMemberPrice = useMemo(() => {
    if (hotelMemberPrice > 0) {
      return hotelMemberPrice + (propertyFees ?? 0)
    }
    return 0
  }, [hotelMemberPrice, propertyFees])
  const showMemberPrice = baseMemberPrice > 0

  const shouldShowDiscount = (shouldShowDiscountPercent || shouldShowBundledDiscountPercent) && pkg.shouldDisplayValue && !offer.isDiscountPillHidden
  const shouldShowValue = hotelValue > (showMemberPrice ? hotelMemberPrice : hotelPrice) && pkg.shouldDisplayValue

  const handleChangePackage = useCallback(() => {
    // TODO: Add change package modal logic here
  }, [])

  return (
    <>
      <Group direction="horizontal" horizontalAlign="space-between" verticalAlign="center" gap={20}>
        <Group direction="vertical" gap={12}>
          <LoadingIndicator visible={isFetching} floating opaque />
          {!offer.bundledWithFlightsOnly && !isFlightsSelected && (
            <div data-testid="pricing">
              <PriceRowPriceCaption>{pluralizeToString('night', pkg.duration)} from</PriceRowPriceCaption>
              <LuxPlusPriceStack
                size="S"
                price={totalPrice}
                memberPrice={totalMemberPrice}
                saleUnit="total"
                offerConfig={offer.luxPlus}
                leadWithLuxPlusPrice
              />
              {shouldShowValue && <PriceRowValueDiscountWithCaption
                data-testid="value-box"
                size="M"
                originalValue={hotelValue}
                discountPercentage={shouldShowDiscount ? highestDiscountPercent : undefined}
              />}
              {showIncludedGuestsExceeded && (
                <AdditionalGuestsPopup
                  complex={false}
                  description="+ Extra guest surcharge"
                  modalContent={pkg.roomPolicyDescription}
                />
              )}
            </div>
          )}
          {isFlightsEnabled && isFlightsSelected && (
            <Group direction="vertical" gap={12}>
              {hasFlights && (
                <div data-testid="pricing">
                  <PriceRowPriceCaption>
                    <><LinePlaneDepartureIcon /> {pluralizeToString('night', pkg.duration)} + flights from</>
                  </PriceRowPriceCaption>
                  <LuxPlusPriceStack
                    size="S"
                    price={totalPrice + flightPrice}
                    memberPrice={totalMemberPrice + flightPrice}
                    saleUnit="total"
                    offerConfig={offer.luxPlus}
                    leadWithLuxPlusPrice
                  />
                  {shouldShowValue && <PriceRowValueDiscountWithCaption
                    data-testid="value-box"
                    size="M"
                    originalValue={hotelValue + flightPrice}
                    discountPercentage={shouldShowDiscount ? highestDiscountPercent : undefined}
                  />}
                  {showIncludedGuestsExceeded && (
                    <AdditionalGuestsPopup
                      complex={false}
                      description="+ Extra guest surcharge"
                      modalContent={pkg.roomPolicyDescription}
                    />
                  )}
                </div>
              )}

              {!hasFlights && flightOrigin?.code === flightDestination?.code && (
                <Group direction="vertical" gap={8}>
                  <PriceRowCaption>
                    No flights available from {flightOrigin?.name}. Please select another departing airport.
                  </PriceRowCaption>
                  <TextButton
                    kind="tertiary"
                    data-filter="airport"
                    onClick={onToggleFilter}
                  >
                    Change airport
                  </TextButton>
                </Group>
              )}

              {!hasFlights && departureTooFarInFuture && <PriceRowCaption>
                Flights can only be booked up to 11 months in advance.
              </PriceRowCaption>}

              {!hasFlights && !flightsBundledNoCache && flightOrigin?.code !== flightDestination?.code && !departureTooFarInFuture && (
                <div>
                  <PriceRowPriceCaption startIcon={<LinePlaneFlyIcon />}>
                    {pluralizeToString('night', pkg.duration)} + flights from
                  </PriceRowPriceCaption>
                  <LuxPlusPriceStack
                    size="S"
                    price={totalPrice}
                    saleUnit="total*"
                    memberPrice={totalMemberPrice}
                    offerConfig={offer.luxPlus}
                    leadWithLuxPlusPrice
                  />
                  {shouldShowValue && <PriceRowValueDiscountWithCaption
                    data-testid="value-box"
                    size="M"
                    originalValue={hotelValue + flightPrice}
                    discountPercentage={shouldShowDiscount ? highestDiscountPercent : undefined}
                  />}
                  <PriceRowTaxesAndFees />
                </div>
              )}
              <AtolProtectedTooltip />
            </Group>
          )}
        </Group>
        {!footerNode && config.PACKAGE_CHANGE_UPGRADE_ENABLED && (
          <Group direction="vertical" gap={8}>
            <TextButton
              kind="secondary"
              fit="flex"
              size="medium"
              startIcon={<LineEditAltIcon />}
              onClick={handleChangePackage}
              disabled={availablePackagesCount <= 1}
            >
              Change room
            </TextButton>
            <Group direction="horizontal" horizontalAlign="center">
              <PriceRowCaption>
                {availablePackagesCount <= 1 && 'No additional options'}
                {availablePackagesCount > 1 && <>{pluralizeToString('room option', availablePackagesCount)} available</>}
              </PriceRowCaption>
            </Group>
          </Group>
        )}

      </Group>

      {!!footerNode && footerNode}
    </>
  )
}

export default withOfferPageAndReduxState((state, appState): MappedProps => ({
  offer: state.offer!,
  checkIn: state.checkInDate,
  airports: appState.geo.airports,
  flightOrigin: state.airport,
  isFlightsSelected: state.flights,
  isLoadingByRoom: isLoadingByRoom(state, appState),
  flightsInfoByRoom: getFlightsInfoByRoom(state, appState),
  availablePackagesByRoom: getAvailablePackagesByRoom(state, appState),
  calendarsToBeFetchedByRoom: getCalendarsToBeFetchedByRoom(state, appState),
}), MobileMultipleRoomsBookingPriceDetails)
